import type {
  PublishTimetableClosure,
  PublishMonthSlot,
  PublishTimetableSlot,
  TimetableEditor,
  TimetableProblemCode,
} from "./schemas/timetable.ts";
import { MONTH_SHORT, shiftDateKey } from "./time.ts";

/**
 * The owner timetable as a draft (prototype `teamna/prototypes/owner-timetable.html`).
 *
 * The server keeps one row per weekday and start (`schedule_templates`). The owner thinks in weekly
 * times instead: "Dolphins, 16:45, Mon and Thu", and in private hours: "Sara is free Mon and Wed,
 * 15:00 to 18:00". A draft item is one of those. It remembers the rows it came from, so a publish
 * keeps them (and the people booked on them) when only the time, the coach or the places change.
 *
 * Pure functions only: the Desk editor and the API both use them, so the problems the owner sees
 * are the problems the server refuses.
 */

export type DraftItem = {
  /** Stable in the editor. A loaded item uses its first row id, a new one `new-N`. */
  key: string;
  groupId: string;
  /** Private hours: a block of private sessions, one every `slotMin` minutes, with one coach. */
  free: boolean;
  /** `Date.getDay` numbers, 0 is Sunday. */
  days: number[];
  /** Minutes after local midnight. A class ends at `start` plus its minutes. */
  start: number;
  end: number;
  /** Null: no limit. Private hours are always 1. */
  capacity: number | null;
  staffUserId: string | null;
  /**
   * The session's own name ("Core awake") and what it is ("Mixed apparatus"), shown to parents.
   * Null or absent: the class name. Optional, so a draft stored before names still loads.
   */
  name?: string | null;
  detail?: string | null;
  /**
   * The rows this item keeps. A class: weekday to row id (`"1"`), so a new time on Monday keeps
   * Monday's row and moves its bookings. Private hours: weekday and start (`"1@960"`), so a booked
   * private session stays at its time while it is inside the hours.
   */
  ids: Record<string, string>;
  /**
   * A copy of the month before (spec `timetable-months.md`): the rows of that month this item came
   * from, keyed like `ids`. The publish sends them as `copiedFrom`, so an unchanged time carries its
   * row (and a child's fixed weekly booking) on into the new month.
   */
  from?: Record<string, string>;
};

/** A closed date. `item` null: the whole business. Set: only that weekly time. */
export type DraftClosure = { date: string; item: string | null; reason: string | null };

export type TimetableDraft = {
  items: DraftItem[];
  /** The last date of every weekly time. Null: no end. */
  until: string | null;
  closures: DraftClosure[];
};

export type DraftGroup = {
  id: string;
  name: string;
  kind: "group" | "private";
  durationMin: number;
  capacity: number | null;
  staffUserId: string | null;
};

export const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
export const WEEKDAY_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;
export const WEEKDAY_PLURAL = ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"] as const;

export function minutesOf(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

/** `HH:MM` for minutes after midnight. */
export function timeOf(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}

/** "4:45 PM" for minutes after midnight: how the owner reads a time. 24:00, the end of a day, is "12:00 AM". */
export function clockOf(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  return `${hours % 12 === 0 ? 12 : hours % 12}:${String(minutes % 60).padStart(2, "0")} ${hours % 24 >= 12 ? "PM" : "AM"}`;
}

export function weekdayOf(dateKey: string): number {
  return new Date(`${dateKey}T00:00:00.000Z`).getUTCDay();
}

/** "Mon 12 Oct" for a `yyyy-MM-dd` key. */
export function formatDateKey(dateKey: string): string {
  const date = new Date(`${dateKey}T00:00:00.000Z`);
  return `${WEEKDAY_SHORT[date.getUTCDay()]} ${date.getUTCDate()} ${MONTH_SHORT[date.getUTCMonth()]}`;
}

/** "Mon, Wed and Thu" */
export function listWords(words: readonly string[]): string {
  if (words.length < 2) return words.join("");
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/**
 * UAE public holidays on a fixed date. The Islamic holidays move with the moon sighting, so the
 * owner closes those dates by hand.
 */
const FIXED_HOLIDAYS: Record<string, string> = {
  "01-01": "New Year's Day",
  "12-01": "Commemoration Day",
  "12-02": "National Day",
  "12-03": "National Day",
};

export function uaePublicHoliday(dateKey: string): string | null {
  return FIXED_HOLIDAYS[dateKey.slice(5)] ?? null;
}

// ---------------------------------------------------------------------------
// Months (spec `teamna/strategy/timetable-months.md`)
// ---------------------------------------------------------------------------

/** Days before the last planned session when the owner is asked to plan the next month (decided 2026-10-17). */
export const PLAN_AHEAD_DAYS = 5;

/**
 * The "Plan the next month" rule, the same on Home and on the Timetable: the last planned session is
 * 5 days away or less, or already past.
 */
export function planNextMonthDue(today: string, lastSession: string | null): boolean {
  if (lastSession === null) return false;
  return Math.round((Date.parse(`${lastSession}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000) <= PLAN_AHEAD_DAYS;
}

/** The first and the last day of a `yyyy-MM` month. */
export function monthRange(month: string): { first: string; last: string } {
  const [year, number] = month.split("-").map(Number) as [number, number];
  // Day 0 of the next month is the last day of this one.
  return { first: `${month}-01`, last: new Date(Date.UTC(year, number, 0)).toISOString().slice(0, 10) };
}

/** The fixed UAE public holidays of a month that are not a weekly day off, as whole-day closed dates. */
export function monthHolidayClosures(month: string, daysOff: readonly number[]): Array<{ date: string; reason: string }> {
  const { first, last } = monthRange(month);
  const out: Array<{ date: string; reason: string }> = [];
  for (let date = first; date <= last; date = shiftDateKey(date, 1)) {
    const reason = uaePublicHoliday(date);
    if (reason && !daysOff.includes(weekdayOf(date))) out.push({ date, reason });
  }
  return out;
}

/**
 * The start of an empty month from the month before: its weekly times as new items (no rows of
 * their own, `from` names the rows they came from), coaches and places kept. Closed dates of the
 * month before stay there; the fixed UAE holidays of the new month come in closed.
 */
export function copyMonthDraft(previous: TimetableDraft, month: string, daysOff: readonly number[], newKey: () => string): TimetableDraft {
  return {
    items: previous.items.map((item) => ({ ...item, key: newKey(), ids: {}, from: { ...item.ids } })),
    until: monthRange(month).last,
    closures: monthHolidayClosures(month, daysOff).map((holiday) => ({ date: holiday.date, item: null, reason: holiday.reason })),
  };
}

// ---------------------------------------------------------------------------
// From the server and back
// ---------------------------------------------------------------------------

export function draftGroups(editor: Pick<TimetableEditor, "groups">): DraftGroup[] {
  return editor.groups.map((group) => ({
    id: group.id,
    name: group.name,
    kind: group.kind,
    durationMin: group.durationMin,
    capacity: group.capacity,
    staffUserId: group.staffUserId,
  }));
}

export function draftFromEditor(editor: Pick<TimetableEditor, "groups" | "until" | "closures">): TimetableDraft {
  const items: DraftItem[] = [];

  for (const group of editor.groups) {
    if (group.kind === "private") {
      // Rows of one coach on one weekday that touch end to start are one block of free hours.
      type Block = { weekday: number; start: number; end: number; staff: string | null; name: string | null; detail: string | null; ids: Record<string, string> };
      const blocks: Block[] = [];
      const rows = [...group.slots].sort(
        (a, b) =>
          (a.staffUserId ?? "").localeCompare(b.staffUserId ?? "") ||
          a.weekday - b.weekday ||
          minutesOf(a.startTime) - minutesOf(b.startTime),
      );
      for (const row of rows) {
        const start = minutesOf(row.startTime);
        const last = blocks[blocks.length - 1];
        if (last && last.weekday === row.weekday && last.staff === row.staffUserId && last.end === start && last.name === (row.name ?? null) && last.detail === (row.detail ?? null)) {
          last.end = start + row.durationMin;
          last.ids[`${row.weekday}@${start}`] = row.id;
        } else {
          blocks.push({
            weekday: row.weekday,
            start,
            end: start + row.durationMin,
            staff: row.staffUserId,
            name: row.name ?? null,
            detail: row.detail ?? null,
            ids: { [`${row.weekday}@${start}`]: row.id },
          });
        }
      }
      // The same hours on several weekdays are one item, like "Sara, Mon and Wed, 15:00 to 18:00".
      const byShape = new Map<string, DraftItem>();
      for (const block of blocks) {
        const shape = `${block.staff ?? ""}|${block.start}|${block.end}|${block.name ?? ""}|${block.detail ?? ""}`;
        const existing = byShape.get(shape);
        if (existing) {
          existing.days.push(block.weekday);
          Object.assign(existing.ids, block.ids);
          continue;
        }
        const item: DraftItem = {
          key: Object.values(block.ids)[0]!,
          groupId: group.id,
          free: true,
          days: [block.weekday],
          start: block.start,
          end: block.end,
          capacity: 1,
          staffUserId: block.staff,
          name: block.name,
          detail: block.detail,
          ids: { ...block.ids },
        };
        byShape.set(shape, item);
        items.push(item);
      }
      continue;
    }

    const byShape = new Map<string, DraftItem>();
    const rows = [...group.slots].sort((a, b) => minutesOf(a.startTime) - minutesOf(b.startTime) || a.weekday - b.weekday);
    for (const row of rows) {
      const start = minutesOf(row.startTime);
      const shape = `${start}|${row.durationMin}|${row.capacity ?? ""}|${row.staffUserId ?? ""}|${row.name ?? ""}|${row.detail ?? ""}`;
      const existing = byShape.get(shape);
      // Two rows on one weekday at one time would share a key; the second becomes its own item.
      if (existing && existing.ids[String(row.weekday)] === undefined) {
        existing.days.push(row.weekday);
        existing.ids[String(row.weekday)] = row.id;
        continue;
      }
      const item: DraftItem = {
        key: row.id,
        groupId: group.id,
        free: false,
        days: [row.weekday],
        start,
        end: start + row.durationMin,
        capacity: row.capacity,
        staffUserId: row.staffUserId,
        name: row.name ?? null,
        detail: row.detail ?? null,
        ids: { [String(row.weekday)]: row.id },
      };
      if (!existing) byShape.set(shape, item);
      items.push(item);
    }
  }

  const owner = new Map<string, string>();
  for (const item of items) for (const id of Object.values(item.ids)) owner.set(id, item.key);
  const closures: DraftClosure[] = [];
  for (const closure of editor.closures) {
    if (closure.templateId === null) {
      closures.push({ date: closure.date, item: null, reason: closure.reason });
      continue;
    }
    const key = owner.get(closure.templateId);
    if (key && !closures.some((row) => row.date === closure.date && row.item === key)) {
      closures.push({ date: closure.date, item: key, reason: closure.reason });
    }
  }

  return { items, until: editor.until, closures };
}

export type DraftSlotOwner = { item: string; weekday: number };

/**
 * The publish request for a draft. `owners[i]` says which item and weekday made `slots[i]`, so a
 * problem the server names by row index points back at a block on the week.
 */
export function publishFromDraft(
  draft: TimetableDraft,
  groups: DraftGroup[],
): { until: string | null; slots: PublishMonthSlot[]; closures: PublishTimetableClosure[]; owners: DraftSlotOwner[] } {
  const groupById = new Map(groups.map((group) => [group.id, group]));
  const slots: PublishMonthSlot[] = [];
  // A copied row goes only with a slot that keeps no row of its own.
  const copied = (item: DraftItem, key: string) => (item.ids[key] ? null : (item.from?.[key] ?? null));
  const owners: DraftSlotOwner[] = [];

  for (const item of draft.items) {
    const group = groupById.get(item.groupId);
    if (!group) continue;
    for (const day of [...item.days].sort((a, b) => a - b)) {
      if (!item.free) {
        slots.push({
          id: item.ids[String(day)] ?? null,
          groupId: item.groupId,
          weekday: day,
          startTime: timeOf(item.start),
          durationMin: item.end - item.start,
          capacity: item.capacity,
          staffUserId: item.staffUserId,
          name: item.name ?? null,
          detail: item.detail ?? null,
          copiedFrom: copied(item, String(day)),
        });
        owners.push({ item: item.key, weekday: day });
        continue;
      }
      const step = group.durationMin;
      for (let minute = item.start; minute + step <= item.end; minute += step) {
        slots.push({
          id: item.ids[`${day}@${minute}`] ?? null,
          groupId: item.groupId,
          weekday: day,
          startTime: timeOf(minute),
          durationMin: step,
          capacity: 1,
          staffUserId: item.staffUserId,
          name: item.name ?? null,
          detail: item.detail ?? null,
          copiedFrom: copied(item, `${day}@${minute}`),
        });
        owners.push({ item: item.key, weekday: day });
      }
    }
  }

  const closures: PublishTimetableClosure[] = [];
  for (const closure of draft.closures) {
    if (closure.item === null) {
      closures.push({ date: closure.date, slot: null, reason: closure.reason });
      continue;
    }
    const weekday = weekdayOf(closure.date);
    owners.forEach((owner, index) => {
      if (owner.item === closure.item && owner.weekday === weekday) {
        closures.push({ date: closure.date, slot: index, reason: closure.reason });
      }
    });
  }

  return { until: draft.until, slots, closures, owners };
}

// ---------------------------------------------------------------------------
// Problems
// ---------------------------------------------------------------------------

export type ProblemSlot = Pick<PublishTimetableSlot, "groupId" | "weekday" | "startTime" | "durationMin" | "staffUserId"> & {
  private: boolean;
};

export type FoundProblem = { code: TimetableProblemCode; slots: number[] };

/**
 * What stops a publish, without the booking counts: one person in two places at the same time,
 * private hours with nobody to run them, a weekly time on a day off. Rows of one person on one
 * weekday that overlap in a chain are one problem.
 */
export function findTimetableProblems(slots: ProblemSlot[], daysOff: readonly number[]): FoundProblem[] {
  const problems: FoundProblem[] = [];

  const offDays = new Map<number, number[]>();
  slots.forEach((slot, index) => {
    if (daysOff.includes(slot.weekday)) offDays.set(slot.weekday, [...(offDays.get(slot.weekday) ?? []), index]);
  });
  for (const indexes of offDays.values()) problems.push({ code: "DAY_OFF", slots: indexes });

  const noCoach = new Map<number, number[]>();
  slots.forEach((slot, index) => {
    if (slot.private && !slot.staffUserId) noCoach.set(slot.weekday, [...(noCoach.get(slot.weekday) ?? []), index]);
  });
  for (const indexes of noCoach.values()) problems.push({ code: "PRIVATE_NEEDS_COACH", slots: indexes });

  const byPersonDay = new Map<string, number[]>();
  slots.forEach((slot, index) => {
    if (!slot.staffUserId) return;
    const key = `${slot.staffUserId}|${slot.weekday}`;
    byPersonDay.set(key, [...(byPersonDay.get(key) ?? []), index]);
  });
  for (const indexes of byPersonDay.values()) {
    const sorted = [...indexes].sort((a, b) => minutesOf(slots[a]!.startTime) - minutesOf(slots[b]!.startTime));
    let cluster: number[] = [];
    let clusterEnd = -1;
    let clash = false;
    const close = () => {
      if (clash && cluster.length > 1) problems.push({ code: "COACH_CLASH", slots: [...cluster].sort((a, b) => a - b) });
      cluster = [];
      clash = false;
    };
    for (const index of sorted) {
      const slot = slots[index]!;
      const start = minutesOf(slot.startTime);
      if (start >= clusterEnd) close();
      else clash = true;
      cluster.push(index);
      clusterEnd = Math.max(clusterEnd, start + slot.durationMin);
    }
    close();
  }

  return problems;
}

/** A limit under the people booked on a future session of a kept row. */
export function findPlacesProblems(
  slots: Array<Pick<PublishTimetableSlot, "id" | "capacity">>,
  mostBooked: (rowId: string) => number,
): Array<FoundProblem & { booked: number }> {
  const out: Array<FoundProblem & { booked: number }> = [];
  slots.forEach((slot, index) => {
    if (!slot.id || slot.capacity === null) return;
    const booked = mostBooked(slot.id);
    if (booked > slot.capacity) out.push({ code: "PLACES_BELOW_BOOKED", slots: [index], booked });
  });
  return out;
}

export type ProblemWords = {
  groupName: (groupId: string) => string;
  personName: (userId: string) => string;
  /** "coach", "instructor" */
  staff: string;
  /** "swimmers", "clients" */
  people: string;
};

/** Plain words for a problem, the same on the editor's note and in the server's answer. */
export function problemDetail(
  problem: FoundProblem & { booked?: number },
  slots: ProblemSlot[],
  words: ProblemWords,
): string {
  const rows = problem.slots.map((index) => slots[index]!).filter(Boolean);
  const first = rows[0];
  if (!first) return "";
  const day = WEEKDAY_LONG[first.weekday]!;

  switch (problem.code) {
    case "DAY_OFF":
      return `${listWords(unique(rows.map((row) => words.groupName(row.groupId))))} is on ${day}. The business is closed on ${WEEKDAY_PLURAL[first.weekday]}.`;
    case "PRIVATE_NEEDS_COACH":
      return `Private hours on ${day} need a ${words.staff}.`;
    case "PLACES_BELOW_BOOKED":
      return `${words.groupName(first.groupId)} on ${WEEKDAY_PLURAL[first.weekday]} has ${problem.booked ?? 0} ${words.people} booked. The limit cannot go below ${problem.booked ?? 0}.`;
    case "COACH_CLASH": {
      // One phrase per group: "Dolphins at 4:45 PM", or for private rows "private sessions 3:00 PM to 4:00 PM".
      const parts: string[] = [];
      for (const groupId of unique(rows.map((row) => row.groupId))) {
        const mine = rows.filter((row) => row.groupId === groupId);
        const start = Math.min(...mine.map((row) => minutesOf(row.startTime)));
        const end = Math.max(...mine.map((row) => minutesOf(row.startTime) + row.durationMin));
        parts.push(
          mine[0]!.private
            ? `private sessions ${clockOf(start)} to ${clockOf(end)}`
            : unique(mine.map((row) => `${words.groupName(groupId)} at ${clockOf(minutesOf(row.startTime))}`)).join(", "),
        );
      }
      const person = first.staffUserId ? words.personName(first.staffUserId) : `The ${words.staff}`;
      return `${person} is in two places on ${day}: ${listWords(parts)}.`;
    }
  }
}

function unique<T>(values: T[]): T[] {
  return [...new Set(values)];
}

// ---------------------------------------------------------------------------
// Counting
// ---------------------------------------------------------------------------

export type CountContext = {
  today: string;
  horizonDays: number;
  daysOff: readonly number[];
};

/** The last date sessions are counted to: the end of the term, or the job's horizon with no end. */
export function lastCountedDate(draft: Pick<TimetableDraft, "until">, context: CountContext): string {
  return draft.until ?? shiftDateKey(context.today, context.horizonDays - 1);
}

/**
 * The end of a term with no end date: the end of this month, or of the next when this month has
 * less than 2 weeks left (asked by Anas, 2026-10-10). The Schedule page opens on it, and the setup
 * step saves it, so a new timetable and the editor agree on its end.
 */
export function defaultEnd(today: string, daysOff: readonly number[]): string {
  const [year, month] = today.split("-").map(Number) as [number, number];
  const soonest = shiftDateKey(today, 13);
  for (const offset of [0, 1]) {
    const end = lastOpenDay(new Date(Date.UTC(year, month + offset, 0)).toISOString().slice(0, 10), daysOff);
    if (end >= soonest) return end;
  }
  return soonest;
}

/**
 * The date itself, or the open day before it when it falls on a day off. A week with no open day
 * gives the date itself: the search looks back one week at most.
 */
export function lastOpenDay(date: string, daysOff: readonly number[]): string {
  for (let back = 0; back < 7; back += 1) {
    const day = shiftDateKey(date, -back);
    if (!daysOff.includes(weekdayOf(day))) return day;
  }
  return date;
}

/** The dates one weekly time runs on, from today to the end, without closed dates. */
export function itemDates(item: DraftItem, draft: TimetableDraft, context: CountContext): string[] {
  const end = lastCountedDate(draft, context);
  const closedDays = new Set(draft.closures.filter((row) => row.item === null).map((row) => row.date));
  const closedForItem = new Set(draft.closures.filter((row) => row.item === item.key).map((row) => row.date));
  const out: string[] = [];
  for (let date = context.today; date <= end; date = shiftDateKey(date, 1)) {
    const weekday = weekdayOf(date);
    if (!item.days.includes(weekday) || context.daysOff.includes(weekday)) continue;
    if (closedDays.has(date) || closedForItem.has(date)) continue;
    out.push(date);
  }
  return out;
}

/** Class sessions in the draft. Private hours are not counted: parents book them one by one. */
export function countSessions(draft: TimetableDraft, context: CountContext): number {
  return draft.items.reduce((total, item) => (item.free ? total : total + itemDates(item, draft, context).length), 0);
}

// ---------------------------------------------------------------------------
// Offers: the two one-click fixes for a person in two places
// ---------------------------------------------------------------------------

function busy(draft: TimetableDraft, person: string, days: number[], start: number, end: number, except: string): boolean {
  return draft.items.some(
    (other) =>
      other.key !== except &&
      other.staffUserId === person &&
      other.days.some((day) => days.includes(day)) &&
      other.start < end &&
      start < other.end,
  );
}

export type ClashOffer = { kind: "person"; userId: string } | { kind: "time"; start: number };

/**
 * Another person free at the same time on every day of the item, and the nearest time the same
 * person is free (15 minute steps, the later one first on a tie, inside `bounds`).
 */
export function clashOffers(
  draft: TimetableDraft,
  itemKey: string,
  team: readonly string[],
  bounds: { start: number; end: number } = { start: 6 * 60, end: 22 * 60 },
): ClashOffer[] {
  const item = draft.items.find((row) => row.key === itemKey);
  if (!item) return [];
  const out: ClashOffer[] = [];
  const other = team.find(
    (userId) => userId !== item.staffUserId && !busy(draft, userId, item.days, item.start, item.end, item.key),
  );
  if (other) out.push({ kind: "person", userId: other });
  if (item.staffUserId) {
    const length = item.end - item.start;
    for (let step = 15; step <= 6 * 60; step += 15) {
      const found = [item.start + step, item.start - step].find(
        (start) =>
          start >= bounds.start &&
          start + length <= bounds.end &&
          !busy(draft, item.staffUserId!, item.days, start, start + length, item.key),
      );
      if (found !== undefined) {
        out.push({ kind: "time", start: found });
        break;
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// What changed: the review lines
// ---------------------------------------------------------------------------

/**
 * A day that came to this item from another weekly time with its row (a coach split or join): the
 * same sessions and bookings, now with this item's coach. `fromStaff` is the coach it had.
 */
export type MovedDay = { day: number; fromStaff: string | null };

export type ItemChange =
  | { kind: "added"; item: DraftItem }
  | { kind: "removed"; item: DraftItem }
  | {
      kind: "changed";
      before: DraftItem;
      item: DraftItem;
      time: boolean;
      length: boolean;
      capacity: boolean;
      person: boolean;
      /** The session name changed. */
      name: boolean;
      /** What the session is ("Mixed apparatus") changed. */
      detail: boolean;
      daysAdded: number[];
      daysRemoved: number[];
      /** Days that came from another weekly time with their row. Not in `daysAdded`. */
      daysMovedIn: MovedDay[];
    }
  | {
      /**
       * A new item that holds days split off other weekly times (only the coach changed for them),
       * and maybe new days added to it after the split.
       */
      kind: "split";
      item: DraftItem;
      daysMovedIn: MovedDay[];
      /** Days with no row before: new sessions. */
      daysAdded: number[];
    };

export function itemChanges(base: TimetableDraft, draft: TimetableDraft): ItemChange[] {
  const out: ItemChange[] = [];
  const before = new Map(base.items.map((item) => [item.key, item]));
  const after = new Map(draft.items.map((item) => [item.key, item]));
  // Where each saved class row sat before, and where it sits now. A row that changed items kept its
  // sessions and bookings: the day moved, it was not taken off and added again.
  const rowBefore = new Map<string, DraftItem>();
  for (const item of base.items) if (!item.free) for (const id of Object.values(item.ids)) rowBefore.set(id, item);
  const rowAfter = new Map<string, string>();
  for (const item of draft.items) if (!item.free) for (const id of Object.values(item.ids)) rowAfter.set(id, item.key);
  const movedIn = (item: DraftItem): MovedDay[] =>
    item.free
      ? []
      : item.days.flatMap((day) => {
          const row = item.ids[String(day)];
          const from = row ? rowBefore.get(row) : undefined;
          return from && from.key !== item.key ? [{ day, fromStaff: from.staffUserId }] : [];
        });
  const movedOut = (old: DraftItem): number[] =>
    old.free
      ? []
      : old.days.filter((day) => {
          const row = old.ids[String(day)];
          const now = row ? rowAfter.get(row) : undefined;
          return now !== undefined && now !== old.key;
        });

  for (const item of draft.items) {
    const old = before.get(item.key);
    if (!old) {
      const carried = movedIn(item);
      if (carried.length > 0) {
        const moved = new Set(carried.map((row) => row.day));
        out.push({ kind: "split", item, daysMovedIn: carried, daysAdded: item.days.filter((day) => !moved.has(day)).sort((a, b) => a - b) });
      } else out.push({ kind: "added", item });
      continue;
    }
    const daysMovedIn = movedIn(item);
    const moved = new Set(daysMovedIn.map((row) => row.day));
    const gone = new Set(movedOut(old));
    const change = {
      kind: "changed" as const,
      before: old,
      item,
      time: old.start !== item.start || (item.free && old.end !== item.end),
      length: !item.free && old.end - old.start !== item.end - item.start,
      capacity: old.capacity !== item.capacity,
      person: old.staffUserId !== item.staffUserId,
      name: (old.name ?? null) !== (item.name ?? null),
      detail: (old.detail ?? null) !== (item.detail ?? null),
      daysAdded: item.days.filter((day) => !old.days.includes(day) && !moved.has(day)).sort((a, b) => a - b),
      daysRemoved: old.days.filter((day) => !item.days.includes(day) && !gone.has(day)).sort((a, b) => a - b),
      daysMovedIn: daysMovedIn.filter((row) => !old.days.includes(row.day)),
    };
    if (
      change.time ||
      change.length ||
      change.capacity ||
      change.person ||
      change.name ||
      change.detail ||
      change.daysAdded.length ||
      change.daysRemoved.length ||
      change.daysMovedIn.length
    ) {
      out.push(change);
    }
  }
  for (const item of removedItems(base, draft)) out.push({ kind: "removed", item });
  return out;
}

/**
 * The saved weekly times that are gone from the draft, with only the days that are really gone. A
 * day that moved to another weekly time with its row (a coach split or join) is not gone: its
 * sessions and bookings live on in that item. So the board draws no ghost for it, the review does
 * not say it stops, and "Put it back" cannot bring its row back as a copy.
 */
export function removedItems(base: TimetableDraft, draft: TimetableDraft): DraftItem[] {
  const keys = new Set(draft.items.map((item) => item.key));
  const rows = new Set<string>();
  for (const item of draft.items) if (!item.free) for (const id of Object.values(item.ids)) rows.add(id);
  const out: DraftItem[] = [];
  for (const item of base.items) {
    if (keys.has(item.key)) continue;
    if (item.free) {
      out.push(item);
      continue;
    }
    const days = item.days.filter((day) => {
      const row = item.ids[String(day)];
      return row === undefined || !rows.has(row);
    });
    if (days.length === 0) continue;
    if (days.length === item.days.length) {
      out.push(item);
      continue;
    }
    const ids: Record<string, string> = {};
    for (const day of days) if (item.ids[String(day)]) ids[String(day)] = item.ids[String(day)]!;
    out.push({ ...item, days, ids });
  }
  return out;
}

/**
 * Gives one day of a class weekly time another coach (a level with two coaches: Mondays with
 * Rami, Wednesdays with Hala). The day keeps its row, so its sessions and bookings stay. It joins
 * the weekly time of that coach at the same time, minutes and places when there is one, or becomes
 * its own item (`newKey`). Closed dates of that day go with it. Returns the key that holds the day.
 */
export function setItemDayStaff(
  draft: TimetableDraft,
  key: string,
  weekday: number,
  staffUserId: string | null,
  newKey: string,
): { draft: TimetableDraft; key: string } {
  const item = draft.items.find((row) => row.key === key);
  if (!item || item.free || !item.days.includes(weekday) || item.staffUserId === staffUserId) return { draft, key };
  const row = item.ids[String(weekday)];
  const source = item.from?.[String(weekday)];
  const target = draft.items.find(
    (other) =>
      other.key !== item.key &&
      !other.free &&
      other.groupId === item.groupId &&
      other.start === item.start &&
      other.end === item.end &&
      other.capacity === item.capacity &&
      other.staffUserId === staffUserId &&
      !other.days.includes(weekday),
  );

  // The only day, and no other weekly time to join: the whole item changes coach.
  if (item.days.length === 1 && !target) {
    return { draft: { ...draft, items: draft.items.map((other) => (other.key === key ? { ...other, staffUserId } : other)) }, key };
  }

  const holder = target?.key ?? newKey;
  const restIds = { ...item.ids };
  delete restIds[String(weekday)];
  const restFrom = { ...item.from };
  delete restFrom[String(weekday)];
  const items: DraftItem[] = [];
  for (const other of draft.items) {
    if (other.key === key) {
      if (item.days.length > 1) items.push({ ...item, days: item.days.filter((day) => day !== weekday), ids: restIds, ...(item.from ? { from: restFrom } : {}) });
      continue;
    }
    if (target && other.key === target.key) {
      items.push({
        ...other,
        days: [...other.days, weekday].sort((a, b) => a - b),
        ids: row ? { ...other.ids, [String(weekday)]: row } : other.ids,
        ...(source ? { from: { ...other.from, [String(weekday)]: source } } : {}),
      });
      continue;
    }
    items.push(other);
  }
  if (!target) {
    items.push({
      ...item,
      key: newKey,
      days: [weekday],
      staffUserId,
      ids: row ? { [String(weekday)]: row } : {},
      ...(item.from ? { from: source ? { [String(weekday)]: source } : {} } : {}),
    });
  }
  // A date closed for this weekly time on that weekday now belongs to the item that holds the day.
  const closures = draft.closures.map((closure) =>
    closure.item === key && (item.days.length === 1 || weekdayOf(closure.date) === weekday) ? { ...closure, item: holder } : closure,
  );
  return { draft: { ...draft, items, closures }, key: holder };
}

export function closureChanges(
  base: TimetableDraft,
  draft: TimetableDraft,
): { added: DraftClosure[]; removed: DraftClosure[] } {
  // A closed date of one weekly time is the date and that day's row, not the item that holds it: a
  // coach split moves the day (and its closure) to another item, and nothing opens or closes.
  const keyIn = (items: DraftItem[]) => {
    const byKey = new Map(items.map((item) => [item.key, item]));
    return (row: DraftClosure) => {
      if (row.item === null) return `${row.date}|`;
      const id = byKey.get(row.item)?.ids[String(weekdayOf(row.date))];
      return id ? `${row.date}|row:${id}` : `${row.date}|${row.item}`;
    };
  };
  const keyBefore = keyIn(base.items);
  const keyAfter = keyIn(draft.items);
  const before = new Set(base.closures.map(keyBefore));
  const after = new Set(draft.closures.map(keyAfter));
  return {
    added: draft.closures.filter((row) => !before.has(keyAfter(row))).sort((a, b) => a.date.localeCompare(b.date)),
    removed: base.closures.filter((row) => !after.has(keyBefore(row))).sort((a, b) => a.date.localeCompare(b.date)),
  };
}

/** How many changes the draft holds: weekly times, closed dates, and the end of the term. */
export function changeCount(base: TimetableDraft, draft: TimetableDraft): number {
  const closures = closureChanges(base, draft);
  return itemChanges(base, draft).length + closures.added.length + closures.removed.length + (base.until === draft.until ? 0 : 1);
}
